var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { html, KompisElement, property } from '@inter-ikea-kompis/web-component';
import { Button as SkapaButton, IconButton as SkapaIconButton } from '@ingka/button-webc/passive.js';
import { registerIcon, warningTriangle } from '@ingka/icon-store';
import { Icon as SkapaIcon } from '@ingka/icon-webc/passive.js';
import { InlineMessage as SkapaInlineMessage } from '@ingka/inline-message-webc/passive.js';
import OpenDesignCodeStateEnum from '../../enums/OpenDesignCodeStateEnum.js';
import OpenDesignCodeInput from '../open-design-code-input/OpenDesignCodeInput.js';
import OpenDesignCodeStyle from './OpenDesignCodeStyle.js';
/**
 * @event open When button "open" is clicked.
 * @event input Emitted when user writes a character in the input field.
 */
export default class OpenDesignCode extends KompisElement {
    constructor() {
        super(...arguments);
        // Localized information. Deprecated - use "translations" instead.
        this.localizedInformation = null;
        // The translations retrieved from translations service.
        this.translations = null;
        // The state. Valid state are in OpenDesignCodeState
        this.state = OpenDesignCodeStateEnum.default;
    }
    /**
     * Renders the component.
     *
     * @returns Template.
     */
    render() {
        // We want translations to be required, but since both props to provide translations are optional, this check is needed until localizedInformation is removed.
        if (!this.hasTranslations()) {
            // We can't throw an error because on the first render no props are set.
            return null;
        }
        const primaryVariant = 'primary';
        return html `
			<style>
				${OpenDesignCodeStyle.getStyle({ theme: this.theme })}
			</style>

			<div class="container">
				<kompis-open-design-code-input
					@open="${this.onOpen.bind(this)}"
					@input="${this.onInput.bind(this)}"
					class="input"
					.theme="${this.theme}"
					.localizedInformation="${this.localizedInformation}"
					.translations="${this.translations}"
					.state="${this.state}"
				></kompis-open-design-code-input>

				<div class="divider"></div>

				<skapa-button
					class="button"
					?disabled="${this.isButtonDisabled()}"
					variant="${primaryVariant}"
					size="small"
					@click="${this.onButtonClick.bind(this)}"
					?loading="${this.state === OpenDesignCodeStateEnum.loading}"
					loadingSrText="${this.getTranslations().loading}"
				>
					${this.getTranslations().sprGalleryOpen}
				</skapa-button>
			</div>

			${this.getInlineMessage()}
		`;
    }
    async connectedCallback() {
        registerIcon(warningTriangle);
        this.customElements.define('skapa-button', SkapaButton);
        this.customElements.define('skapa-icon-button', SkapaIconButton);
        this.customElements.define('skapa-icon', SkapaIcon);
        this.customElements.define('skapa-inline-message', SkapaInlineMessage);
        this.customElements.define('kompis-open-design-code-input', OpenDesignCodeInput);
        super.connectedCallback();
    }
    hasTranslations() {
        return !!this.translations || !!this.localizedInformation?.translations;
    }
    getTranslations() {
        const translations = this.translations
            ? this.translations
            : this.localizedInformation?.translations;
        return translations;
    }
    getInlineMessage() {
        return html `<div aria-live="assertive">${this.getUnavailableDesignErrorMessage()}</div>`;
    }
    getUnavailableDesignErrorMessage() {
        if (this.state !== OpenDesignCodeStateEnum.failed) {
            return null;
        }
        const error = this.getTranslations().invalidConfigurationMessage;
        const cautionaryVariant = 'cautionary';
        return html `
			<skapa-inline-message subtle variant="${cautionaryVariant}">
				<skapa-icon slot="message-icon" icon="warning-triangle"></skapa-icon>
				<span slot="body">${error}</span>
			</skapa-inline-message>
		`;
    }
    isButtonDisabled() {
        if (this.state === OpenDesignCodeStateEnum.loading) {
            return false;
        }
        return !this.getOpenDesignCodeInput()?.isValidVpcCode();
    }
    onOpen() {
        if (!this.isButtonDisabled()) {
            this.onButtonClick();
        }
    }
    onInput({ detail }) {
        this.requestUpdate();
        this.dispatchEvent(new CustomEvent('input', {
            bubbles: true,
            detail
        }));
    }
    onButtonClick() {
        this.dispatchEvent(new CustomEvent('open', {
            bubbles: true,
            detail: {
                vpcCode: this.getOpenDesignCodeInput()?.getVpcCode() ?? ''
            }
        }));
    }
    getOpenDesignCodeInput() {
        return (this.shadowRoot.querySelector(this.customElements.getInternalName('kompis-open-design-code-input')) ?? null);
    }
}
__decorate([
    property({ type: Object, required: true })
], OpenDesignCode.prototype, "theme", void 0);
__decorate([
    property({ type: Object })
], OpenDesignCode.prototype, "localizedInformation", void 0);
__decorate([
    property({ type: Object })
], OpenDesignCode.prototype, "translations", void 0);
__decorate([
    property({ type: String })
], OpenDesignCode.prototype, "state", void 0);
//# sourceMappingURL=OpenDesignCode.js.map